// Pre-fetch the section HTML for every variant after window.load + idle, so
// the customer's click-to-fetch later resolves from an in-memory cache
// instead of waiting on a 150-300ms Section Rendering API roundtrip.
//
// IMPORTANT: Shopify section responses carry `Cache-Control: private, no-store`
// (set by the storefront CDN because Set-Cookie is present in the response),
// so the browser's HTTP cache CANNOT hold them. We work around that by
// storing the response text in `window.__variantSectionCache` (a Map keyed
// by request URL). product-info.js's renderProductInfo consults this cache
// before firing its fetch — see the cache-lookup block there.
//
// Companion to assets/product-variant-image-preload.js — that script warms
// the *image* HTTP cache (image responses ARE cacheable). Together they
// make a same-product variant switch effectively free: ~5ms JS critical
// path + setActiveMedia + a Map.get(url) instead of a network roundtrip.
//
// URL format matches product-info.js's buildRequestUrlWithParams:
//   <productUrl>?section_id=<id>&variant=<variantId>
// We mirror it exactly so the in-memory cache key lines up.
//
// JNCO uses two-option variants (Waist × Inseam, 28+ combos per product) so
// instead of enumerating option pairs we read the variant IDs directly from
// the <data-product-variants> JSON the variant picker emits and prefetch by
// variant ID. With v5/snippets/product-variant-picker.liquid now emitting
// real option_value_ids, the runtime click resolves to a variant ID via
// findVariantByIds and hits buildRequestUrlWithParams's `?variant=` branch —
// the same key we prefetch under.
//
// Skipped:
//   - Currently-selected variant (its HTML is already on the page).
//   - Variants flagged available=false in the JSON (their section render
//     would just show "Notify me" — fine to fetch on-demand instead of
//     warming a sold-out path).
//   - Cross-product color swatches (separate <a> links in JNCO's color
//     group, navigate as full pages; out of scope here).

(function () {
  // Shared in-memory cache. product-info.js reads from this in
  // renderProductInfo before firing its fetch. Initialized before the
  // auto-run guard so a second load of this script still produces a
  // working cache Map.
  window.__variantSectionCache = window.__variantSectionCache || new Map();

  function preloadVariantSections() {
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    var productUrl = productInfo.dataset.url;
    // Match what product-info.js's sectionId getter returns:
    //   return this.dataset.originalSection || this.dataset.section;
    // so the prefetch URL has the same cache key as the click-time fetch.
    var sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    if (!productUrl || !sectionId) return;

    var variantScript = productInfo.querySelector('variant-selects [data-product-variants]');
    if (!variantScript) return;

    var variants;
    try {
      variants = JSON.parse(variantScript.textContent || '[]');
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    // Currently-selected variant — skip it (already on the page).
    var selectedScript = productInfo.querySelector('variant-selects [data-selected-variant]');
    var selectedId = null;
    if (selectedScript && selectedScript.textContent) {
      try {
        selectedId = (JSON.parse(selectedScript.textContent) || {}).id || null;
      } catch (e) {
        // ignore
      }
    }

    variants.forEach(function (variant) {
      if (!variant || !variant.id) return;
      if (variant.available === false) return; // sold-out path — fine on demand
      if (selectedId && String(variant.id) === String(selectedId)) return;

      // Build the URL the same way buildRequestUrlWithParams does so the
      // cache key lines up byte-for-byte with the click-time fetch.
      var parsedUrl;
      try {
        parsedUrl = new URL(productUrl, window.location.origin);
      } catch (e) {
        return;
      }
      parsedUrl.searchParams.set('section_id', sectionId);
      parsedUrl.searchParams.set('variant', variant.id);
      parsedUrl.searchParams.delete('option_values');
      var url = parsedUrl.pathname + parsedUrl.search;

      // Skip if already cached (page-load preload + re-trigger from
      // embedded contexts; idempotent so repeat calls are cheap).
      if (window.__variantSectionCache.has(url)) return;

      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (text) {
          // Validate the response is actually a section render — not a 200
          // password page, throttling soft-page, login redirect, or generic
          // error template. Those all pass `r.ok` and easily exceed 100 bytes
          // but contain no <product-info>. Caching one would blank the PDP
          // on the next click for that variant: DOMParser →
          // querySelector('product-info') → null → HTMLUpdateUtility's
          // viewTransition hides the live <product-info> via display:none
          // while trying to insert null.
          if (!text || text.length < 100) return;
          if (!text.includes('<product-info')) return;
          window.__variantSectionCache.set(url, text);
        })
        .catch(function () {});
    });
  }

  function schedule() {
    var idle =
      window.requestIdleCallback ||
      function (cb) {
        return setTimeout(cb, 1500);
      };
    // Section HTML is bigger per request (~100KB) than images and the
    // customer is unlikely to switch in the first second after page load.
    idle(preloadVariantSections, { timeout: 4000 });
  }

  // Expose so any context that injects a PDP via Section Rendering API
  // after page load can re-trigger the scan once the embedded
  // <product-info> exists. Idempotent — preloadVariantSections skips URLs
  // already in __variantSectionCache.
  window.__variantSectionPreloadRun = schedule;

  // Auto-run once on initial page load — guarded so a second load of this
  // script on the same page doesn't double-schedule.
  if (window.__variantSectionPreloadStarted) return;
  window.__variantSectionPreloadStarted = true;

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
})();
